import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Search, X } from 'lucide-react';
import api from '@/lib/api';
import { DocBox, FieldLabel, DOC_FIELD } from './DocumentChrome';

/**
 * Who the document is addressed to.
 *
 * Picking a customer fills the block from the record and keeps `customer_id`
 * linked, which is what the grouped-by-customer view reads. Every filled field
 * stays editable afterwards: a quotation often goes to a different address or a
 * different contact at the same firm, and that must not mean unlinking the
 * customer or editing the customer record to send one document.
 */
export default function PartyPicker({ docType, customerId, customerName, billTo, onPick, onUnlink, onChange }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!debounced.trim()) { setMatches([]); return; }
    let live = true;
    api.get('/customers', { params: { search: debounced, limit: 10 } })
      .then((res) => { if (live) setMatches(res.data.customers || []); })
      .catch(() => { if (live) setMatches([]); });
    return () => { live = false; };
  }, [debounced]);

  const showResults = open && !!query.trim();

  return (
    <DocBox
      heading={docType.partyHeading}
      actions={customerId && (
        <button
          type="button"
          onClick={onUnlink}
          className="flex items-center gap-1 rounded px-1 text-[10px] font-normal normal-case tracking-normal text-white/70 hover:bg-white/20"
          data-testid="unlink-customer"
        >
          <X className="h-3 w-3" /> unlink
        </button>
      )}
    >
      <div className="p-2">
        {customerId ? (
          <p className="mb-1 truncate text-[11px] text-doc-teal" data-testid="linked-customer">
            Linked to {customerName}
          </p>
        ) : (
          <div className="relative mb-1">
            <Search className="pointer-events-none absolute left-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder="Search a customer on file…"
              data-testid="party-customer-search"
              className="h-7 pl-7 text-xs"
            />
            {showResults && (
              <div className="absolute inset-x-0 top-full z-20 mt-0.5 max-h-52 divide-y overflow-y-auto rounded border border-slate-200 bg-white shadow-lg">
                {matches.length === 0 ? (
                  <p className="px-2.5 py-2 text-[11px] text-slate-400">
                    No customer matches that — type the party details in below instead.
                  </p>
                ) : matches.map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    onMouseDown={() => { onPick(customer); setQuery(''); setOpen(false); }}
                    data-testid={`party-customer-${customer.id}`}
                    className="block w-full px-2.5 py-1.5 text-left hover:bg-slate-50"
                  >
                    <span className="block truncate text-[12px] text-slate-700">{customer.customer_name}</span>
                    <span className="block truncate text-[10px] text-slate-400">
                      {customer.firm_name || '—'} · {customer.mobile_number || 'no phone'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <Input
          value={billTo.name ?? ''}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Client / Company Name"
          aria-label="Party name"
          data-testid="bill-to-name"
          className={`${DOC_FIELD} text-[13px] font-bold`}
        />

        <FieldLabel className="mt-1">Address</FieldLabel>
        <Textarea
          rows={2}
          value={billTo.address ?? ''}
          onChange={(e) => onChange({ address: e.target.value })}
          placeholder="Street, City, State, PIN"
          aria-label="Party address"
          data-testid="bill-to-address"
          className="min-h-0 resize-none border-0 bg-transparent px-1 py-0 text-sm shadow-none focus-visible:ring-1"
        />

        <FieldLabel className="mt-1">GSTIN / PAN</FieldLabel>
        <Input
          value={billTo.gstin ?? ''}
          onChange={(e) => onChange({ gstin: e.target.value.toUpperCase() })}
          placeholder="GSTIN / PAN Number"
          aria-label="Party GSTIN or PAN"
          data-testid="bill-to-gstin"
          className={`${DOC_FIELD} font-mono`}
        />

        <FieldLabel className="mt-1">{docType.contactLabel}</FieldLabel>
        <Input
          value={billTo.contact ?? ''}
          onChange={(e) => onChange({ contact: e.target.value })}
          placeholder={docType.contactPlaceholder}
          aria-label="Party contact"
          data-testid="bill-to-contact"
          className={DOC_FIELD}
        />
      </div>
    </DocBox>
  );
}
